import { Box, Breadcrumbs as MuiBreadcrumbs, Link, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Breadcrumbs Component
 * Shows navigation path and allows quick navigation
 */
const Breadcrumbs = ({ navItems = [] }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin, isProvider, profile } = useAuth();

  // Get default route based on role
  const getDefaultRoute = () => {
    if (isAdmin) return '/admin';
    if (isProvider) return '/clinic-dashboard';
    if (profile?.tenant_role === 'sponsor') return '/sponsor-dashboard';
    return '/dashboard';
  };

  // Build breadcrumb path from current location
  const buildBreadcrumbs = () => {
    const pathnames = location.pathname.split('/').filter((x) => x);
    const breadcrumbs = [];

    // Add home/dashboard
    breadcrumbs.push({
      label: isAdmin ? 'Admin' : isProvider ? 'Clinic' : profile?.tenant_role === 'sponsor' ? 'Sponsor' : 'Dashboard',
      path: getDefaultRoute(),
      icon: <HomeIcon fontSize="small" />,
    });

    // Build path segments
    let currentPath = '';
    pathnames.forEach((segment, index) => {
      currentPath += `/${segment}`;
      
      // Find matching nav item
      const findNavItem = (items, path) => {
        for (const item of items) {
          if (item.path === path) {
            return item;
          }
          if (item.children) {
            for (const child of item.children) {
              if (child.path === path) {
                return child;
              }
            }
          }
        }
        return null;
      };

      const navItem = findNavItem(navItems, currentPath);
      const label = navItem?.label || segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');

      // Don't add if it's the last segment (current page)
      if (index < pathnames.length - 1) {
        breadcrumbs.push({
          label,
          path: currentPath,
        });
      } else {
        // Current page (not clickable)
        breadcrumbs.push({
          label,
          path: null, // null means current page
        });
      }
    });

    return breadcrumbs;
  };

  const breadcrumbs = buildBreadcrumbs();

  // Don't show breadcrumbs if we're on the home page
  if (breadcrumbs.length <= 1) {
    return null;
  }

  return (
    <Box sx={{ mb: 2 }}>
      <MuiBreadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb navigation"
        sx={{
          '& .MuiBreadcrumbs-ol': {
            flexWrap: 'nowrap',
          },
        }}
      >
        {breadcrumbs.map((crumb, index) => {
          const isLast = index === breadcrumbs.length - 1;

          if (isLast || !crumb.path) {
            return (
              <Typography
                key={index}
                color="text.primary"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontWeight: 500,
                }}
              >
                {index === 0 && crumb.icon}
                {crumb.label}
              </Typography>
            );
          }

          return (
            <Link
              key={index}
              component="button"
              variant="body2"
              onClick={() => navigate(crumb.path)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color: 'text.secondary',
                textDecoration: 'none',
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                  color: 'primary.main',
                },
              }}
            >
              {index === 0 && crumb.icon}
              {crumb.label}
            </Link>
          );
        })}
      </MuiBreadcrumbs>
    </Box>
  );
};

export default Breadcrumbs;

